"use client"

import Image from "next/image"
import { Button } from "@/components/atoms"
import { HttpTypes } from "@medusajs/types"
import { cn } from "@/lib/utils"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { getProductPrice } from "@/lib/helpers/get-product-price"
import { Product } from "@/types/product"
import { PartPlaceholder } from "@/components/atoms/PartImage/PartImage"

// عنوان المنتج مخزَّن «الاسم — ⁨الرقم⁩». يُفصلان هنا لسببين:
// الرقم يحمل شرطة فينكسر عند نهاية السطر ويظهر نصفاه مقلوبين في فقرة عربية،
// والرقم يُقرأ من اليسار وحدةً واحدة فيستحق سطره واتجاهه.
const splitTitle = (title?: string | null) => {
  const raw = String(title || "")
  const at = raw.indexOf(" — ")
  if (at === -1) return { name: raw, number: "" }
  return {
    name: raw.slice(0, at),
    number: raw.slice(at + 3).replace(/[⁦-⁩]/g, ""),
  }
}

export const ProductCard = ({
  product,
  className,
}: {
  product: HttpTypes.StoreProduct | Product,
  className?: string
}) => {
  if (!product) {
    return null
  }

  const { cheapestPrice } = getProductPrice({ product: product as HttpTypes.StoreProduct })

  const productName = String(product.title || "Product")
  const { name: partName, number: partNumber } = splitTitle(product.title)

  return (
    <div
      className={cn(
        "relative group border rounded-sm flex flex-col justify-between p-1 w-full lg:w-[calc(20%-1rem)] xl:w-[calc(16.666%-1rem)] min-w-[170px]",
        className
      )}
      data-testid="product-card"
      data-product-handle={product.handle}
    >
      <div className="relative w-full h-full bg-primary aspect-square" data-testid="product-card-image-container">
        <LocalizedClientLink
          href={`/products/${product.handle}`}
          aria-label={`View ${productName}`}
          title={`View ${productName}`}
          data-testid="product-card-link"
        >
          <div className="overflow-hidden rounded-sm w-full h-full flex justify-center align-center ">
            {product.thumbnail ? (
              <Image
                priority
                fetchPriority="high"
                src={decodeURIComponent(product.thumbnail)}
                alt={`${productName} image`}
                width={100}
                height={100}
                sizes="(min-width: 1280px) 17vw, (min-width: 1024px) 20vw, (min-width: 640px) 33vw, 50vw"
                className="object-contain aspect-square w-full object-center h-full lg:group-hover:-mt-14 transition-all duration-300 rounded-xs"
                data-testid="product-card-image"
              />
            ) : (
              <PartPlaceholder group={(product as any).categories?.[0]?.name} name={(product as any).title} />
            )}
          </div>
        </LocalizedClientLink>
        <LocalizedClientLink
          href={`/products/${product.handle}`}
          aria-label={`See more about ${productName}`}
          title={`See more about ${productName}`}
        >
          <Button className="absolute rounded-sm bg-action text-action-on-primary h-auto lg:h-[48px] lg:group-hover:block hidden w-full uppercase bottom-1 z-10" data-testid="product-card-see-more-button">
            See More
          </Button>
        </LocalizedClientLink>
      </div>
      <LocalizedClientLink
        href={`/products/${product.handle}`}
        aria-label={`Go to ${productName} page`}
        title={`Go to ${productName} page`}
      >
        <div className="flex justify-between p-4" data-testid="product-card-info">
          <div className="w-full">
            <h3 className="heading-sm line-clamp-2 leading-snug" data-testid="product-card-title">{partName}</h3>
            {partNumber ? (
              <p className="mt-1 text-xs text-gray-500 truncate" dir="ltr" data-testid="product-card-number">
                {partNumber}
              </p>
            ) : null}
            <div className="flex items-center gap-2 mt-2" data-testid="product-card-price">
              <p className="font-medium" data-testid="product-card-current-price">{cheapestPrice?.calculated_price}</p>
              {cheapestPrice?.calculated_price !==
                cheapestPrice?.original_price && (
                <p className="text-sm text-gray-500 line-through" data-testid="product-card-original-price">
                  {cheapestPrice?.original_price}
                </p>
              )}
            </div>
          </div>
        </div>
      </LocalizedClientLink>
    </div>
  )
}
